import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../core/config/api";
import "./payment_details.css";
import { couponService } from "../../services/couponService";
import { useHubSpotScript } from "../../utils/useHubSpotScript";

type PaymentState = {
  planId: number | null;
  planName: string;
  description?: string | null;
  price: number;
  currency: string;
  interval: string;
  base_credits: number;
  credit_notes: string | null;
  features?: Record<string, any> | null;
  isNewSignup?: boolean;
};

// Tax is not yet calculated by the backend — kept as a single source of truth.
const TAX_RATE = 0;

const Payment_Details = () => {
  useHubSpotScript();

  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as PaymentState | null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  // Discount percentage returned by the coupon backend (0 = no coupon applied).
  const [discountPercent, setDiscountPercent] = useState(0);
  // The coupon code that was actually accepted, so display and payload agree.
  const [appliedCode, setAppliedCode] = useState("");
  const [fetchedPlan, setFetchedPlan] = useState<PaymentState | null>(null);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const [fetchPlanError, setFetchPlanError] = useState("");

  const planIdParam = new URLSearchParams(location.search).get("plan_id");
  // Prefer router state (navigated from pricing page); fall back to URL-fetched plan.
  const effectiveState = state?.planName ? state : fetchedPlan;

  // Fetch plan by ID when page is opened via a shared payment link.
  useEffect(() => {
    if (state?.planName || !planIdParam) return;
    setFetchingPlan(true);
    setFetchPlanError("");
    apiClient
      .get(`/pricing/plans/${planIdParam}`)
      .then((res) => {
        const p = res.data;
        setFetchedPlan({
          planId: p.id,
          planName: p.name,
          description: p.description ?? null,
          price: Number(p.price) || 0,
          currency: p.currency,
          interval: p.billing_interval,
          base_credits: Number(p.base_credits) || 0,
          credit_notes: p.credit_notes ?? null,
          features: p.features ?? null,
        });
      })
      .catch(() =>
        setFetchPlanError("Failed to load plan details. Please check your link and try again.")
      )
      .finally(() => setFetchingPlan(false));
  }, [planIdParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Billing math — must stay above any conditional returns (rules of hooks).
  const { subtotal, discountAmount, total } = useMemo(() => {
    const sub = Number(effectiveState?.price) || 0;
    const discount = +((sub * discountPercent) / 100).toFixed(2);
    const taxable = Math.max(sub - discount, 0);
    const tax = +(taxable * TAX_RATE).toFixed(2);
    return {
      subtotal: sub,
      discountAmount: discount,
      taxAmount: tax,
      total: +(taxable + tax).toFixed(2),
    };
  }, [effectiveState?.price, discountPercent]);

  if (fetchingPlan) {
    return (
      <div className="container">
        <div className="keep-box-in-center">
          <div className="payment-page">
            <div className="payment-card" style={{ textAlign: "center", padding: "2rem" }}>
              <p>Loading plan details…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetchPlanError) {
    return (
      <div className="container">
        <div className="keep-box-in-center">
          <div className="payment-page">
            <div className="payment-card">
              <h2>Unable to load plan</h2>
              <p>{fetchPlanError}</p>
              <button type="button" className="confirm-btn" onClick={() => navigate("/pricing")}>
                Go to Pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!effectiveState || !effectiveState.planName) {
    return (
      <div className="container">
        <div className="keep-box-in-center">
          <div className="payment-page">
            <div className="payment-card">
              <h2>No plan selected</h2>
              <p>Please choose a plan from the pricing page.</p>
              <button
                type="button"
                className="confirm-btn"
                onClick={() => navigate("/pricing")}
              >
                Go to Pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    planId,
    planName,
    description,
    interval,
    base_credits,
    features,
  } = effectiveState;

  const creditsNum = base_credits || 0;
  const promoApplied = discountPercent > 0;

  const formatMoney = (amount: number) =>
    `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;

  const formatInterval = (val: string) => {
    if (!val) return "";
    const lower = val.toLowerCase();
    if (lower === "one_time") return "one-time";
    if (lower === "yearly") return "year";
    if (lower === "monthly") return "month";
    return val.replace("_", " ");
  };

  const featuresList = () => {
    if (!features) {
      return [
        `${creditsNum.toLocaleString()} credits per month`,
        "Single & bulk verification",
        "SMTP verification",
        "CSV upload & export",
        "Verification history",
        "Email support",
      ];
    }
    if (Array.isArray(features)) return features as string[];
    return Object.values(features).filter((v) => typeof v === "string") as string[];
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) {
      setPromoError("Please enter a promo code.");
      return;
    }

    try {
      setLoading(true);
      setPromoError("");

      const data = await couponService.verifyCoupon(code);

      if (data?.valid && Number(data.discount_value) > 0) {
        setDiscountPercent(Number(data.discount_value));
        setAppliedCode(code);
        setPromoError("");
      } else {
        setDiscountPercent(0);
        setAppliedCode("");
        setPromoError("Invalid coupon code.");
      }
    } catch (err: any) {
      // Backend sends the exact reason in `detail` (404 -> "Invalid coupon code",
      // 400 -> inactive / expired / usage-limit messages).
      setPromoError(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to verify coupon. Please try again."
      );
      setDiscountPercent(0);
      setAppliedCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setDiscountPercent(0);
    setAppliedCode("");
    setPromoCode("");
    setPromoError("");
  };

  const handleConfirmPay = async () => {
    if (!planId) {
      setError("Missing plan information. Please go back to pricing and try again.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Ask the backend to create a Stripe-hosted checkout session for this plan.
      // NOTE: the current BE endpoint only consumes `plan_id`. `coupon_code` is
      // forwarded so the discount can be applied once the BE checkout endpoint
      // adds coupon support — it is safely ignored until then.
      const { data } = await apiClient.post("/stripe-payment/create-checkout-session", {
        plan_id: planId,
        coupon_code: promoApplied ? appliedCode : null,
        is_new_signup: !!effectiveState?.isNewSignup,
      });

      if (data?.checkout_url) {
        // Leave the SPA and hand off to Stripe's hosted checkout page.
        // Stripe redirects back to /payment/success?session_id=... or /payment/cancel.
        window.location.href = data.checkout_url;
        return;
      }

      setError(data?.message || "Could not start checkout. Please try again.");
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        "Failed to process payment. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="keep-box-in-center">
        <img
          src="/images/right-emails-logo.svg"
          className="form-logo"
          alt="Right Emails"
        />

        <div className="payment-page">
          <div className="payment-card">
            <div className="payment-plan">
              <div className="plan-header">
                <span>{planName}</span>
                <span className="plan-price">
                  {formatMoney(subtotal)}{" "}
                  <span className="payent-per-year">
                    / {formatInterval(interval)}
                  </span>
                </span>
              </div>

              <ul className="plan-features">
                {featuresList().map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <div className="plan-footer">
                {description || "Best for → Individuals, freelancers, early users"}
              </div>
            </div>

            <div className="payment-details">
              <h2>Payment Details</h2>

              {error && (
                <div
                  className="alert alert-danger"
                  role="alert"
                  style={{ marginBottom: "1rem" }}
                >
                  {error}
                </div>
              )}
              {success && (
                <div
                  className="alert alert-success"
                  role="alert"
                  style={{ marginBottom: "1rem" }}
                >
                  {success}
                </div>
              )}

              <div className="summary-box">
                    <div className="summary-row">
                      <span className="total-total">Total</span>
                      <span className="one-egt">{formatMoney(subtotal)}</span>
                    </div>

                    {promoApplied && (
                      <div className="summary-row">
                        <span className="total-total">Discount</span>
                        <span className="one-egt">
                          -{formatMoney(discountAmount)}
                        </span>
                      </div>
                    )}

                    <div className="payment-line"></div>

                    <div className="summary-row total-row">
                      <span className="tax-include">Total (Including Tax)</span>
                      <span className="total-pill">{formatMoney(total)}</span>
                    </div>
                  </div>

                  <p className="promo-text">Have a promo code?</p>

                  <div className="promo-box">
                    <div style={{ position: "relative", display: "flex", alignItems: "center", flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        disabled={loading}
                        style={{ width: "100%", paddingRight: promoCode ? "32px" : undefined }}
                      />

                      {promoCode && (
                        <span
                          onClick={handleRemovePromo}                        
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            fontSize: "12px",
                            color: "#005066",
                            userSelect: "none",
                            fontWeight: "600"
                          }}
                        >
                          ✕
                        </span>
                      )}
                    </div>
                    <button

                      type="button"

                      onClick={handleApplyPromo}

                      disabled={loading || !promoCode.trim()}

                      style={{

                        backgroundColor:

                          promoCode.trim() && !loading

                            ? "#007799"

                            : "#C2C8CE",

                        color: "#feffff",

                        cursor:

                          promoCode.trim() && !loading

                            ? "pointer"

                            : "not-allowed",

                        opacity: loading ? 0.7 : 1,

                      }}
                    >

                      Apply
                    </button>
                  </div>


                  {promoError && (
                    <p className="promo-error" role="alert">
                      {promoError}
                    </p>
                  )}

                  {promoApplied && !promoError && (
                    <p className="cupon-code-applied">✓ Coupon code applied</p>
                  )}

                  <button
                    type="button"
                    className="confirm-btn"
                    onClick={handleConfirmPay}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Confirm And Pay"}
                  </button>
            </div>
          </div>

          <div className="trust-badges">
            <div className="trust-item">
              <img
                className="trust-icon"
                src="/images/secure&private.png"
                alt=""
                aria-hidden="true"
              />
              <span>Secure &amp; private</span>
            </div>

            <div className="trust-item">
              <img
                className="trust-icon"
                src="/images/no-spam.png"
                alt=""
                aria-hidden="true"
              />
              <span>No spam, no hidden charges</span>
            </div>

            <div className="trust-item">
              <img
                className="trust-icon"
                src="/images/trusted.png"
                alt=""
                aria-hidden="true"
              />
              <span>Trusted for accurate email verification</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Payment_Details;
